import DBStore from "@/core/db_store";
import Swal from "sweetalert2";
import { computed, h, nextTick, reactive, ref, watch } from "vue";
import { edit } from "@/core/db";
import { openPanel } from "@/layouts/layouts";
import { getModel } from "@/tables";
import { genGUID, isAsync, deepMerge } from "@/helpers/utils";
import Export from "./Export.vue";
import { tableExport } from "@/api/db";
import { query } from "@/api/db";
import Bus from "@/core/bus";

export default class DBGridController {
  public store: DBStore;
  public form: any;
  public field: any;
  public verified: any;
  public valid: any;
  public feedback: any;
  public readonly: any;
  public access: any;
  public settings: any;
  public panelFun: any;
  public standardCommands: any;
  public pages: any;
  public page: any;
  public limit: any;
  public searchText: any;
  public stateField: any;
  public rows: any;
  public sortingFields: any;
  public footerData: any;
  public sortedFields: any;

  constructor(protected props: any, protected emit: any) {
    this.verified = ref(false);
    this.valid = ref(false);
    this.feedback = ref("");
    this.searchText = ref("");
    this.pages = ref(0);
    this.page = ref(0);
    this.panelFun = ref([]);
    this.rows = ref([]);
    this.sortingFields = reactive({});

    this.footerData = reactive({});

    this.form = props.form;
    this.field = props.field;

    this.store = props.store ? props.store : this.createStore();

    if (!this.store?.model?.access) {
      this.store.model.access = {
        read: false,
        create: false,
        update: false,
        delete: false,
      };
    }

    this.access = this.store.model.access;

    this.readonly = computed(() => {
      return !(
        this.access.create ||
        this.access.update ||
        this.access.delete
      ) ||
        props.readonly ||
        this.store.state.readonly ||
        props?.form?.readonly?.value
        ? true
        : false;
    });

    // Вычисляемое свойство для сортировки полей по order
    this.sortedFields = computed(() => {
      const fields = this.store.state.fields;
      if (!fields) return [];

      const fieldKeys = Object.keys(fields);

      // Сортируем ключи по order
      fieldKeys.sort((a, b) => {
        const orderA = fields[a]?.config?.order ?? fieldKeys.indexOf(a);
        const orderB = fields[b]?.config?.order ?? fieldKeys.indexOf(b);
        return orderA - orderB;
      });

      return fieldKeys;
    });

    this.settings = reactive({
      id: null,
      filters: [],
      orderBy: {},
      menu: true,
      numbering: false,
      sorting: false,
      slectMode: false,
      searchpanel: true,
      limits: [25, 50, 75, 100, 1000, 3000],
      limit: 25,
      owner: null, //id - зависимого поля
      panelFun: true,
      footer: false,
      actions: {
        view: false,
        edit: true,
        create: true,
        copy: true,
        delete: true,
      },
      contextMenuBody: [],
      contextMenuHead: [],
      default: {},
      localStorageKey: null,
      edit: {}, //Настройки компонента редактирования
      height: null,
      autoOpen: true,
      deleteText: "Удалить запись?",
      crossot: () => false, //Перечеркивание
      export: true,
      transparentTr: false,
      styleTr: () => ({}),
      classTr: () => ({}),
      rowSelect: false,
      onCreate: () => null,
      onRowSelect: (data: any, checked: boolean) => {
        this.store.selectRow(data, checked);
      },
      onBeforeNew: async (params: any) => await edit(params),
      onBeforeEdit: async (params: any) => await edit(params),
      onBeforeCopy: async (params: any) => await edit(params),
      onKeyDown: async () => true,
      cellClick: {},
      settingsKey: null, // Уникальный ключ для сохранения настроек таблицы на бэкенде
    });

    Object.assign(this.settings, props.config);

    // Применяем конфигурацию из stateField.config для вложенных таблиц
    if (this.form && this.field) {
      const field = this.getFieldModel(
        this.field.split("."),
        this.form.store.state,
      );
      if (field?.config?.actions) {
        Object.assign(this.settings.actions, field.config.actions);
      }
    }

    this.limit = ref(this.settings.limit);

    // Flag to track initial settings load to avoid unnecessary saves
    let isInitialLoad = true;

    if (Object.keys(this.settings.orderBy).length > 0) {
      Object.assign(this.sortingFields, this.settings.orderBy);

      this.onSort(this.settings.orderBy, false);
    }

    this.standardCommands = {
      edit: {
        caption: "Изменить",
        title: "Изменить",
        icon: <i class="icon icon-edit"></i>,
        class: "btn btn-edit",
        onClick: () => this.editRow(),
      },
      view: {
        caption: "Просмотр",
        title: "Просмотр",
        icon: <i class="icon icon-eye"></i>,
        class: "btn btn-edit",
        onClick: () => this.editRow(),
      },
      add: {
        caption: "Добавить",
        title: "Добавить",
        icon: <i class="icon icon-plus-circle"></i>,
        class: "btn btn-add",
        onClick: () => this.addRow(),
      },
      copy: {
        caption: "Копировать",
        title: "Копировать",
        icon: <i class="icon icon-file"></i>,
        class: "btn btn-copy",
        onClick: () => this.copyRow(),
      },
      delete: {
        caption: "Удалить",
        title: "Удалить",
        icon: <i class="icon icon-x-circle"></i>,
        class: "btn btn-delete",
        // onClick: () => this.deleteRow()
        onClick: () => this.deleteSelected(),
      },
    };

    nextTick(() => {
      if (this.readonly.value) {
        this.createPanelFun([
          {
            view: this.settings.view ? {} : null,
          },
        ]);
      } else {
        // Проверяем actions - если это функции, вызываем их, иначе используем как булево значение
        const canEdit =
          this.access.update &&
          (typeof this.settings.actions.edit === "function"
            ? this.settings.actions.edit()
            : this.settings.actions.edit);
        const canCreate =
          this.access.create &&
          (typeof this.settings.actions.create === "function"
            ? this.settings.actions.create()
            : this.settings.actions.create);
        const canCopy =
          this.access.create &&
          (typeof this.settings.actions.copy === "function"
            ? this.settings.actions.copy()
            : this.settings.actions.copy);
        const canDelete =
          this.access.delete &&
          (typeof this.settings.actions.delete === "function"
            ? this.settings.actions.delete()
            : this.settings.actions.delete);

        this.createPanelFun([
          {
            edit: canEdit ? {} : null,
            add: canCreate ? {} : null,
            copy: canCopy ? {} : null,
            delete: canDelete ? {} : null,
          },
        ]);
      }
    });

    watch(
      () => this.searchText.value,
      async (newValue, oldValue) => {
        // Вызываем fetchData только если значение действительно изменилось
        if (String(newValue) !== String(oldValue)) {
          await this.fetchData({ page: 0 });
        }
      },
    );

    watch(
      () => this.limit.value,
      async (newLimit, oldLimit) => {
        await this.fetchData({ page: 0 });
        // Mark initial load as complete before checking save condition
        if (isInitialLoad) {
          isInitialLoad = false;
        }
        // Save pagination settings when limit changes (skip on initial load)
        if (!isInitialLoad && newLimit !== oldLimit) {
          await this.saveColumnSettings();
        }
      },
    );
  }

  getFieldModel(fields: any, structure: any): any {
    const field: any = fields.shift();

    if (fields.length > 0) {
      return this.getFieldModel(fields, structure.fields[field].model);
    } else {
      return structure.fields[field];
    }
  }

  createStore() {
    // обновлять позиции
    if (!this.field || typeof this.field !== "string") {
      console.error(
        "Ошибка: this.field не определен или не является строкой",
        this.field,
      );
      return null; // Или другое поведение по умолчанию
    }

    const field = deepMerge(
      {},
      this.getFieldModel(this.field.split("."), this.form.store.state),
    );

    const store = new DBStore(
      field?.table ? field.table : `table_${this.field}`,
    );

    store.model = field.type.table;
    store.model.subtable = true;

    this.stateField = this.getFieldModel(
      this.field.split("."),
      this.form.store.state,
    );

    store.createState(this.stateField);

    const access = this.form.store.model.access
      ? this.form.store.model.access
      : {
          read: true,
          create: true,
          update: true,
          delete: true,
        };

    const editable =
      "access" in this.stateField
        ? store.availabeRole(this.stateField.access, this.field, field)
        : access.create || access.update || access.delete;

    store.model.access = {
      read: true,
      create: editable,
      update: editable,
      delete: editable,
    };

    if (field.depends) {
      store.model.depends = field.depends;

      store.owner = this.form.store.getValue(field.depends);

      if (!store.owner) {
        store.owner = this.form.store.setValue(field.depends, genGUID());
      }
    }

    const data = this.form.store.getValue(this.field);

    if (data) {
      store.data.rows = data;
      store.data.position = data.length ? 0 : -1;
    } else {
      const newData = this.form.store.setValue(this.field, []);

      store.data.rows = newData;
      store.data.position = newData.length ? 0 : -1;
    }

    store.files = this.form.store.files;

    return store;
  }

  createPanelFun(items: any) {
    this.panelFun.value.splice(0, this.panelFun.value.length);

    for (const item of items) {
      const panel: any = {};

      for (const key in item) {
        if (item[key]) {
          panel[key] = {};

          if (key in this.standardCommands) {
            Object.assign(panel[key], this.standardCommands[key], item[key]);
          } else {
            Object.assign(panel[key], item[key]);
          }
        }
      }

      this.panelFun.value.push(panel);
    }
  }

  validation() {
    if (this.form && this.field) {
      const field = this.form.store.state.fields[this.field];

      if (this.props.validation && field.validation) {
        for (const i in field.validation) {
          const result = field.validation[i](this.store.data.rows);
          if (result !== false) {
            this.verified.value = true;
            this.valid.value = false;
            this.feedback.value = result;

            return result;
          }
        }
      }
    }

    this.verified.value = false;
    this.valid.value = true;
    this.feedback.value = "";

    return false;
  }

  home() {
    this.activeRow(0);
  }

  end() {
    this.activeRow(this.store.data.rows.length - 1);
  }

  focusRow() {
    const position = this.store.data.position;
    if (
      position != -1 &&
      this.store.data.rows.length > position &&
      this.rows.value[position]
    ) {
      this.rows.value[position].focus();
    }
  }

  activeRow(position: number) {
    this.store.setPosition(position);

    this.focusRow();

    this.emit("active", this.store.data.rows[position]);
  }

  positionsUpdate() {
    const model = this.store.model;

    if (model.subtable && model.positionField) {
      const rows = this.store.data.rows;

      for (const postion in rows) {
        rows[postion][model.positionField] = Number(postion) + 1;
      }
    }
  }

  async addRow(newData = {}) {
    if (!this.readonly.value && this.access.create) {
      const data = {
        ...(typeof this.settings.default == "function"
          ? isAsync(this.settings.default)
            ? await this.settings.default()
            : this.settings.default()
          : this.settings.default),
        ...newData,
      };

      const response: any = await query({
        table: this.store.name,
        method: "new",
        data,
      });

      const params = Object.assign({
        store: this.store,
        data: response.complete ? response.data : data,
        config: this.settings.edit,
      });

      if (this.store.model.ownerField) {
        if (this.store.owner) {
          params.data.owner = this.store.owner;
        } else {
          console.error("Не указан владелец");

          return;
        }
      }

      await this.settings.onBeforeNew(params);

      this.positionsUpdate();

      this.focusRow();

      this.verified.value && this.validation();
    }
  }

  async copyRow(defaults = {}) {
    //Возвращать фокус на грид
    const data = this.store.currentData();
    if (data) {
      if (!this.readonly.value && this.access.create) {
        await this.settings.onBeforeCopy({
          copy: true,
          store: this.store,
          data,
          config: this.settings.edit,
          defaults,
        });

        this.positionsUpdate();

        this.verified.value && this.validation();

        this.focusRow();
      }
    }
  }

  async openRow(data: any) {
    const params: any = Object.assign({
      store: this.store,
      data,
      config: this.settings.edit,
    });

    if (this.store.model.ownerField) {
      if (this.store.owner) {
        params.data.owner = this.store.owner;
      } else {
        console.error("Не указан владелец");

        return;
      }
    }

    if (this.settings.actions.view || this.readonly.value) {
      params.readonly = true;

      await this.settings.onBeforeEdit(params);

      this.positionsUpdate();

      this.focusRow();
    } else {
      if (!this.readonly.value && this.access.update) {
        params.owner = params.store.owner;

        await this.settings.onBeforeEdit(params);

        this.positionsUpdate();

        this.focusRow();

        this.verified.value && this.validation();
      }
    }
  }

  async editRow() {
    //Возвращать фокус на грид
    const data = this.store.currentData();

    if (data) this.openRow(data);
  }

  getSelected() {
    const result: any[] = [];

    if (this.store.data.select.length > 0) {
      this.store.data.select.forEach((el: any) => result.push(el));
    } else {
      if (this.store.currentData()) {
        result.push(this.store.currentData().id);
      }
    }
    return result;
  }
  async deleteSelected() {
    if (!this.readonly.value && this.access.delete) {
      const selectedItems = this.getSelected();
      const data = this.store.data.rows.find(
        (el: any) => el.id === selectedItems[0],
      );
      if (!this.store?.model || (await this.store.model.onBeforeDelete(data))) {
        if (selectedItems.length === 0) {
          Swal.fire({
            title: "Нет выбранных записей",
            text: "Пожалуйста, выберите записи для удаления.",
            icon: "info",
          });
          return;
        }

        Swal.fire({
          title: `Удалить выбранные записи (${selectedItems.length})?`,
          text: "Это действие нельзя отменить!",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Да, удалить",
          cancelButtonText: "Отмена",
        }).then(async (result) => {
          if (result.isConfirmed) {
            for (const id of selectedItems) {
              // Находим запись по ID

              const row = this.store.data.rows.find((el: any) => el.id === id);
              if (row) {
                // Выполняем удаление
                if (
                  !this.store.model ||
                  (await this.store.model.onBeforeDelete(row))
                ) {
                  await this.store.deleteRecord(row);

                  // Post-delete действия
                  this.store?.model &&
                    (await this.store.model.onAfterDelete(row));
                }
              } else {
                console.warn("Запись с ID не найдена:", id);
              }
            }

            // Обновляем отображение
            this.positionsUpdate();
            this.verified.value && this.validation();
            this.focusRow();

            // Отправляем событие для обновления данных
            Bus.$emit("refresh-data", { table: this.store.name });

            Swal.fire({
              title: "Удалено!",
              text: `${selectedItems.length} записи были успешно удалены.`,
              icon: "success",
            });
          }
        });
      }
    }
  }

  async deleteRow() {
    if (!this.readonly.value && this.access.delete) {
      const data = this.store.currentData();
      if (data) {
        if (
          !this.store?.model ||
          (await this.store.model.onBeforeDelete(data))
        ) {
          Swal.fire({
            title: this.settings.deleteText,
            showCancelButton: true,
            confirmButtonText: "Да",
            cancelButtonText: "Отмена",
          }).then(async ({ value }) => {
            if (value) {
              await this.store.deleteRecord(data);

              this.positionsUpdate();

              this.store?.model && (await this.store.model.onAfterDelete(data));

              // Отправляем событие для обновления данных
              Bus.$emit("refresh-data", { table: this.store.name });

              this.verified.value && this.validation();

              this.focusRow();
            }
          });
        }
      }
    }
  }

  selectRow(data: any = this.store.currentData()) {
    if (this.settings.slectMode) {
      this.emit("select", data);
    } else {
      this.editRow();
    }
  }

  setFields(fields: any = []) {
    const config: any = {};

    if (fields.length > 0) {
      config.fields = {};

      for (const field of fields) {
        // Сначала проверяем store.state.fields, затем исходную конфигурацию
        const originalField =
          this.store.state.fields[field.name] ||
          this.props.config?.fields?.[field.name];

        if (originalField) {
          config.fields[field.name] = Object.assign({}, originalField);

          Object.assign(config.fields[field.name], {
            description: field.description,
          });

          if (!config.fields[field.name].config) {
            config.fields[field.name].config = {};
          }
          config.fields[field.name].config.visible = field.visible;

          // Сохраняем order из позиции в массиве
          if (field.order !== undefined) {
            config.fields[field.name].config.order = field.order;
          }
        }
      }
    } else {
      //При сбросе настроек отображаются все колонки
      const schema = getModel(this.store.name);
      if (schema) {
        const _fields = this.props.config?.fields;

        if (_fields) config.fields = deepMerge({}, _fields);
      }
    }

    this.store.createState(config);
  }

  contextMenuHead() {
    const items = [];

    if (!this.store.model.subtable) {
      for (const key in this.store.state.fields) {
        const field = this.store.state.fields[key];
        if (field?.config?.sorting) {
          items.push({
            icon: <i class="icon icon-arrow-loop"></i>,
            caption: "Сброс сортировки",
            onClick: async () => await this.resetSorting(),
          });

          break;
        }
      }

      items.push({
        icon: <i class="icon icon-arrow-sync"></i>,
        caption: "Обновить",
        onClick: async () => await this.fetchData(),
      });
    }

    // Show table settings menu if we have a settings key (from settingsKey or store.name)
    const settingsKey = this.getSettingsKey();
    if (this.settings.localStorageKey || settingsKey) {
      items.push({
        icon: <i class="icon icon-cog"></i>,
        caption: "Настройка таблицы",
        onClick: async () => {
          // Используем поля из конфигурации, если они есть, иначе все поля из store
          let fieldsForSettings =
            this.settings.fieldsForSettings || this.store.state.fields;

          // Загружаем сохраненные настройки (бэкенд с приоритетом, затем localStorage)
          try {
            const savedSettings = await this.loadColumnSettings();
            if (savedSettings) {
              // Применяем limit (пагинация)
              if (savedSettings.limit !== undefined && savedSettings.limit !== null) {
                this.limit.value = savedSettings.limit;
              }

              if (savedSettings?.fields) {
                // Применяем сохраненные настройки к fieldsForSettings
                const savedFieldsMap: any = {};
                for (const key in savedSettings.fields) {
                  const savedField = savedSettings.fields[key];
                  savedFieldsMap[key] = {
                    visible: savedField.visible,
                    width: savedField.width,
                    order: savedField.order,
                  };
                }

                // Применяем сохраненные настройки к полям
                const updatedFields: any = {};
                for (const key in fieldsForSettings) {
                  updatedFields[key] = { ...fieldsForSettings[key] };
                  if (savedFieldsMap.hasOwnProperty(key)) {
                    // Обновляем видимость
                    if (savedFieldsMap[key].visible !== undefined) {
                      if (!updatedFields[key].config) {
                        updatedFields[key].config = {};
                      }
                      updatedFields[key].config.visible = savedFieldsMap[key].visible;
                    }
                    // Обновляем ширину
                    if (savedFieldsMap[key].width !== undefined) {
                      if (!updatedFields[key].config) {
                        updatedFields[key].config = {};
                      }
                      updatedFields[key].config.width = savedFieldsMap[key].width;
                    }
                    // Обновляем порядок
                    if (savedFieldsMap[key].order !== undefined) {
                      if (!updatedFields[key].config) {
                        updatedFields[key].config = {};
                      }
                      updatedFields[key].config.order = savedFieldsMap[key].order;
                    }
                  }
                }
                fieldsForSettings = updatedFields;
              }
            }
          } catch (e) {
            console.error("Error loading saved settings:", e);
          }

          openPanel({
            scrollable: true,
            modal: true,
            caption: "Настройка таблицы",
            onCreate: (panel: any) => ({
              component: h(Export, { fields: fieldsForSettings }),
              buttons: [
                {
                  class: "btn btn-action",
                  caption: "Сохранить",
                  onClick: async () => {
                    const config = panel.ref.getFields();

                    // Сохраняем через saveColumnSettings (поддерживает и бэкенд, и localStorage)
                    this.setFields(config);
                    await this.saveColumnSettings();

                    panel.close();
                  },
                },
                {
                  class: "btn btn-action",
                  caption: "Сброс настроек",
                  onClick: async () => {
                    // Удаляем настройки из localStorage
                    if (this.settings.localStorageKey) {
                      localStorage.removeItem(this.settings.localStorageKey);
                    }

                    // Удаляем настройки с бэкенда
                    if (settingsKey) {
                      try {
                        const { tableSettingsApi } = await import(
                          "@/api/tableSettings"
                        );
                        await tableSettingsApi.deleteSettings(settingsKey);
                      } catch (error) {
                        console.error(
                          "Failed to delete settings from backend:",
                          error,
                        );
                      }
                    }

                    this.setFields();

                    panel.close();
                  },
                },
              ],
            }),
          });
        },
      });
    }

    if (this.settings.export && !this.store.model.subtable) {
      items.push({
        icon: <i class="icon icon-export"></i>,
        caption: "Экспорт таблицы",
        onClick: async () => {
          openPanel({
            modal: true,
            scrollable: true,
            caption: "Экспорт таблицы",
            onCreate: (panel: any) => ({
              component: h(Export, { fields: this.store.state.fields }),
              buttons: [
                {
                  class: "btn btn-action",
                  caption: "Экспорт",
                  onClick: async () => {
                    const options = {
                      fields: panel.ref.getFields(),
                      options: this.store.fetchParams(),
                    };

                    const data: any = await tableExport(
                      this.store.name,
                      options,
                    );
                    const blob = new Blob([data], {
                      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                    });

                    const link = document.createElement("a");

                    link.download = "export.xlsx";

                    link.href = URL.createObjectURL(blob);

                    link.click();

                    URL.revokeObjectURL(link.href);

                    panel.close();
                  },
                },
              ],
            }),
          });
        },
      });
    }

    return items.concat(
      typeof this.settings.contextMenuHead == "function"
        ? this.settings.contextMenuHead()
        : this.settings.contextMenuHead,
    );
  }

  contextMenuBody(data: any) {
    const items = [];

    if (this.readonly.value || this.settings.actions.view) {
      items.push({
        icon: <i class="icon icon-eye"></i>,
        caption: "Просмотр",
        onClick: () => this.editRow(),
      });
    } else {
      const canEdit =
        typeof this.settings.actions.edit === "function"
          ? this.settings.actions.edit()
          : this.settings.actions.edit;
      const canCreate =
        typeof this.settings.actions.create === "function"
          ? this.settings.actions.create()
          : this.settings.actions.create;
      const canCopy =
        typeof this.settings.actions.copy === "function"
          ? this.settings.actions.copy()
          : this.settings.actions.copy;
      const canDelete =
        typeof this.settings.actions.delete === "function"
          ? this.settings.actions.delete()
          : this.settings.actions.delete;

      if (canEdit && this.access.update)
        items.push({
          icon: <i class="icon icon-edit"></i>,
          caption: "Изменить",
          onClick: () => this.editRow(),
        });

      if (this.access.create) {
        if (canCreate) {
          items.push({
            icon: <i class="icon icon-plus-circle"></i>,
            caption: "Добавить",
            onClick: () => this.addRow(),
          });
        }

        if (canCopy) {
          items.push({
            icon: <i class="icon icon-file"></i>,
            caption: "Копировать",
            onClick: () => this.copyRow(),
          });
        }
      }

      if (canDelete && this.access.delete)
        items.push({
          icon: <i class="icon icon-x-circle"></i>,
          caption: "Удалить",
          // onClick: () => this.deleteRow()
          onClick: () => this.deleteSelected(),
        });
    }

    return items.concat(
      typeof this.settings.contextMenuBody == "function"
        ? this.settings.contextMenuBody(data)
        : this.settings.contextMenuBody,
    );
  }

  async onKeyDown(event: any) {
    const rowData: any = this.store.data.rows[this.store.data.position]
      ? this.store.data.rows[this.store.data.position]
      : {};

    if (await this.settings.onKeyDown(event, rowData, this)) {
      switch (event.keyCode) {
        case 13: //enter
          this.selectRow();

          break;

        case 32: //space
          if (this.settings.rowSelect && this.store.data.position != null) {
            event.preventDefault();

            const id = rowData.id;

            this.settings.onRowSelect(
              rowData,
              !this.store.data.select.includes(id),
            );
          }

          break;

        case 45: //insert
          const canCreate =
            typeof this.settings.actions.create === "function"
              ? this.settings.actions.create()
              : this.settings.actions.create;
          if (canCreate) this.addRow();

          break;

        case 46: //delete
          const canDelete =
            typeof this.settings.actions.delete === "function"
              ? this.settings.actions.delete()
              : this.settings.actions.delete;
          if (canDelete)
            // this.deleteRow();
            this.deleteSelected();

          break;

        case 113: //F2
          const canEdit =
            typeof this.settings.actions.edit === "function"
              ? this.settings.actions.edit()
              : this.settings.actions.edit;
          if (canEdit) this.editRow();

          break;

        case 37: //left
          event.preventDefault();

          if (event.ctrlKey) {
            if (this.page.value > 0)
              await this.fetchData({ page: this.page.value - 1 });
          } else this.home();

          this.focusRow();

          break;

        case 39: //right
          event.preventDefault();

          if (event.ctrlKey) {
            if (this.page.value < this.pages.value - 1)
              await this.fetchData({ page: this.page.value + 1 });
          } else this.end();

          this.focusRow();

          break;

        case 38: //up
          event.preventDefault();

          if (
            this.store.data.position != null &&
            this.store.data.position > 0
          ) {
            if (event.ctrlKey) {
              this.rowUp();
            } else {
              this.activeRow(this.store.data.position - 1);
            }

            this.focusRow();
          }
          break;

        case 40: //down
          event.preventDefault();

          if (
            this.store.data.position != null &&
            this.store.data.position < this.store.data.rows.length - 1
          ) {
            if (event.ctrlKey) {
              this.rowDown();
            } else {
              this.activeRow(this.store.data.position + 1);
            }

            this.focusRow();
          }

          break;

        case 36: //home
          event.preventDefault();

          if (event.ctrlKey) {
            await this.fetchData({ page: 0 });
          } else {
            this.home();
          }

          this.focusRow();

          break;

        case 35: //end
          event.preventDefault();

          if (event.ctrlKey && this.pages.value > 0) {
            await this.fetchData({ page: this.pages.value - 1 });
          } else {
            this.end();
          }

          this.focusRow();

          break;
      }
    }
  }

  async onSort(sort: any, fetch = true) {
    this.store.fetchOptions.orderby = [];

    for (const key in sort) {
      const field: any = this.store.state.fields[key];

      if (field && field.config && field.config.sorting) {
        this.store.fetchOptions.orderby.push(
          field.config.sorting + (sort[key] ? "" : " desc"),
        );
      }
    }

    if (fetch) await this.fetchData();
  }

  async sort(sort: any, fetch = true) {
    for (const key in this.sortingFields) {
      delete this.sortingFields[key];
    }

    Object.assign(this.sortingFields, sort);

    this.onSort(this.sortingFields, fetch);
  }

  async selectPage(page: any) {
    await this.fetchData({ page });
  }

  sortByPosition() {
    const model = this.store.model;

    if (model.subtable && model.positionField) {
      const { rows } = this.store.data;

      rows.sort(
        (a: any, b: any) => a[model.positionField] - b[model.positionField],
      );
    }
  }

  rowUp() {
    this.positionsUpdate();

    const model = this.store.model;

    if (model.subtable && model.positionField) {
      const { position, rows } = this.store.data;

      const posA = rows[position][model.positionField];
      const posB = rows[position - 1][model.positionField];

      rows[position][model.positionField] = posB;
      rows[position - 1][model.positionField] = posA;

      this.store.setPosition(position - 1);

      setTimeout(() => this.focusRow(), 0);
    }

    this.sortByPosition();
  }

  rowDown() {
    this.positionsUpdate();

    const model = this.store.model;

    if (model.subtable && model.positionField) {
      const { position, rows } = this.store.data;

      const posA = rows[position][model.positionField];
      const posB = rows[position + 1][model.positionField];

      rows[position][model.positionField] = posB;
      rows[position + 1][model.positionField] = posA;

      this.store.setPosition(position + 1);

      setTimeout(() => this.focusRow(), 0);
    }

    this.sortByPosition();
  }

  async resetSorting() {
    this.store.fetchOptions.orderby.splice(
      0,
      this.store.fetchOptions.orderby.length,
    );

    for (const key in this.sortingFields) {
      delete this.sortingFields[key];
    }

    if (Object.keys(this.settings.orderBy).length > 0) {
      Object.assign(this.sortingFields, this.settings.orderBy);

      this.onSort(this.settings.orderBy, false);
    }

    await this.fetchData();
  }

  async fetchData(options: any = {}) {
    if ("page" in options) {
      this.page.value = options.page;

      this.store.fetchOptions.page = options.page;
    }

    this.store.fetchOptions.searchtext = this.searchText.value;
    this.store.fetchOptions.limit = this.limit.value;

    await this.store.fetchData(options);

    this.pages.value = this.store.pages;

    if ("page" in options) {
      this.store.fetchOptions.page = 0;
    } else {
      this.page.value = 0;
    }
  }

  /**
   * Update column width
   * @param fieldKey - Field key
   * @param width - New width in pixels
   */
  updateColumnWidth(fieldKey: string, width: number) {
    const field = this.store.state.fields[fieldKey];
    if (field && field.config) {
      // Сохраняем ширину как строку с "px" для согласованности с GridHead.vue
      field.config.width = `${width}px`;
      this.saveColumnSettings();
    }
  }

  /**
   * Reorder columns by moving one column before/after another
   * @param fromKey - Key of column to move
   * @param toKey - Key of target column
   * @param position - 'before' or 'after'
   */
  reorderColumns(fromKey: string, toKey: string, position: "before" | "after") {
    const fields = this.store.state.fields;
    const fieldKeys = Object.keys(fields);

    const fromIndex = fieldKeys.indexOf(fromKey);
    const toIndex = fieldKeys.indexOf(toKey);

    if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
      return;
    }

    // Get order values
    const orders = fieldKeys.map((key, index) => ({
      key,
      order: fields[key].config?.order ?? index,
    }));

    // Sort by current order
    orders.sort((a, b) => a.order - b.order);

    // Find indices in sorted array
    const sortedFromIndex = orders.findIndex((o) => o.key === fromKey);
    const sortedToIndex = orders.findIndex((o) => o.key === toKey);

    if (sortedFromIndex === -1 || sortedToIndex === -1) {
      return;
    }

    // Calculate new order value
    let newOrder: number;
    if (position === "before") {
      if (sortedToIndex === 0) {
        newOrder = orders[0].order - 1;
      } else {
        const prevOrder = orders[sortedToIndex - 1].order;
        newOrder = (prevOrder + orders[sortedToIndex].order) / 2;
      }
    } else {
      if (sortedToIndex === orders.length - 1) {
        newOrder = orders[orders.length - 1].order + 1;
      } else {
        const nextOrder = orders[sortedToIndex + 1].order;
        newOrder = (orders[sortedToIndex].order + nextOrder) / 2;
      }
    }

    // Update order for moved field
    if (fields[fromKey].config) {
      fields[fromKey].config.order = newOrder;
    }

    this.saveColumnSettings();
  }

  /**
   * Get the settings key for this table
   * Uses settingsKey if provided, otherwise falls back to store.name
   */
  getSettingsKey(): string | null {
    return this.settings.settingsKey || this.store?.name || null;
  }

  /**
   * Save column settings to localStorage and/or backend
   * Includes field settings and pagination limit
   */
  async saveColumnSettings() {
    const config: { fields: Record<string, any>; limit?: number } = { fields: {} };

    // Save limit (pagination)
    config.limit = this.limit.value;

    for (const key in this.store.state.fields) {
      const field = this.store.state.fields[key];
      if (field?.config) {
        config.fields[key] = {
          description: field.description,
          visible: field.config.visible,
          width: field.config.width,
          order: field.config.order,
        };
      }
    }

    // Get settings key from settingsKey or store.name
    const settingsKey = this.getSettingsKey();

    // Save to backend if settingsKey is available
    if (settingsKey) {
      try {
        const { tableSettingsApi } = await import("@/api/tableSettings");
        await tableSettingsApi.saveSettings(settingsKey, config);
      } catch (error) {
        console.error("Failed to save settings to backend:", error);
        // Fallback to localStorage
        if (this.settings.localStorageKey) {
          localStorage[this.settings.localStorageKey] = JSON.stringify(config);
        }
      }
    } else if (this.settings.localStorageKey) {
      // Save to localStorage if localStorageKey is provided
      localStorage[this.settings.localStorageKey] = JSON.stringify(config);
    }
  }

  /**
   * Load column settings from backend or localStorage
   * @returns Settings object with fields and limit or null
   */
  async loadColumnSettings(): Promise<{ fields: Record<string, any>; limit?: number } | null> {
    // Get settings key from settingsKey or store.name
    const settingsKey = this.getSettingsKey();

    // Try to load from backend first if settingsKey is available
    if (settingsKey) {
      try {
        const { tableSettingsApi } = await import("@/api/tableSettings");
        const response: any = await tableSettingsApi.getSettings(settingsKey);
        // API возвращает данные напрямую: { success: true, data: {...} }
        if (response?.complete && response?.data) {
          return response.data;
        }
      } catch (error) {
        console.error("Failed to load settings from backend:", error);
        // Fallback to localStorage
        if (
          this.settings.localStorageKey &&
          localStorage[this.settings.localStorageKey]
        ) {
          try {
            return JSON.parse(localStorage[this.settings.localStorageKey]);
          } catch {
            return null;
          }
        }
      }
    } else if (
      this.settings.localStorageKey &&
      localStorage[this.settings.localStorageKey]
    ) {
      // Load from localStorage if localStorageKey is provided
      try {
        return JSON.parse(localStorage[this.settings.localStorageKey]);
      } catch {
        return null;
      }
    }

    return null;
  }

  /**
   * Apply loaded settings to fields and pagination
   * @param settings - Settings object with fields and optional limit
   */
  applyColumnSettings(settings: { fields: Record<string, any>; limit?: number }) {
    if (!settings) return;

    // Apply pagination limit
    if (settings.limit !== undefined && settings.limit !== null) {
      this.limit.value = settings.limit;
    }

    if (!settings?.fields) return;

    for (const key in settings.fields) {
      const savedField = settings.fields[key];
      const field = this.store.state.fields[key];

      if (field && savedField && field.config) {
        if (savedField.width !== undefined) {
          // Нормализуем ширину: если число, добавляем "px"
          const width = savedField.width;
          field.config.width = typeof width === "number" ? `${width}px` : width;
        }
        if (savedField.order !== undefined) {
          field.config.order = savedField.order;
        }
        if (savedField.visible !== undefined) {
          field.config.visible = savedField.visible;
        }
      }
    }
  }
}
